import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from 'lucide-react';
import { contactData } from '../data/mock';
import { usePractice } from '../context/PracticeContext';
import { formatHours } from '../data/practice';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const navigate = useNavigate();
  const { address, hours } = usePractice();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    condition: '',
    message: '',
    preferredContact: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      const refId = res.data?.referenceId || res.data?.reference_id;
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        condition: '',
        message: '',
        preferredContact: 'email'
      });
      if (refId) {
        navigate(`/thank-you?ref=${encodeURIComponent(refId)}&type=enquiry`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : "Something went wrong. Please try again or call us directly.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}?text=Hello! I'd like to book a consultation`, '_blank');
  };

  return (
    <section id="contact" className="contact-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Ready to start your journey to relief? Contact us today
          </p>
        </div>

        <div className="contact-grid">
          <div className="contact-info">
            <h3 className="contact-info-title">Contact Information</h3>
            
            <div className="contact-item">
              <div className="contact-icon">
                <Phone size={20} />
              </div>
              <div>
                <div className="contact-label">Phone</div>
                <a href={`tel:${contactData.phone}`} className="contact-value">
                  {contactData.phone}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <Mail size={20} />
              </div>
              <div>
                <div className="contact-label">Email</div>
                <a href={`mailto:${contactData.email}`} className="contact-value">
                  {contactData.email}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <MapPin size={20} />
              </div>
              <div>
                <div className="contact-label">Location</div>
                <div className="contact-value" data-testid="contact-address">
                  The TMD Physio<br />
                  {address.street}, {address.city}<br />
                  {address.postcode}
                </div>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <Clock size={20} />
              </div>
              <div>
                <div className="contact-label">Hours</div>
                {hours.map((h) => (
                  <div key={h.day} className="contact-value" data-testid={`contact-hours-${h.day.toLowerCase()}`}>
                    <strong>{h.day}:</strong> {formatHours(h)}
                  </div>
                ))}
              </div>
            </div>

            <Button 
              className="whatsapp-btn"
              onClick={handleWhatsApp}
            >
              <MessageCircle className="w-5 h-5" />
              Message on WhatsApp
            </Button>
          </div>

          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  data-testid="contact-name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+44 7700 900000"
                    data-testid="contact-phone"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="condition">Primary Concern *</Label>
                <select
                  id="condition"
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  required
                  className="form-select"
                  data-testid="contact-condition"
                >
                  <option value="">Select your condition</option>
                  <option value="tmd">TMD / Jaw Pain</option>
                  <option value="headaches">Headaches</option>
                  <option value="migraines">Migraines</option>
                  <option value="tinnitus">Tinnitus</option>
                  <option value="facial-pain">Facial Pain</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <Label htmlFor="message">Tell us about your symptoms *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Please describe your symptoms, how long you've had them, and what you've tried so far..."
                  rows={5}
                  data-testid="contact-message"
                />
              </div>

              <div className="form-group">
                <Label>Preferred Contact Method *</Label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="email"
                      checked={formData.preferredContact === 'email'}
                      onChange={handleChange}
                    />
                    <span>Email</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="phone"
                      checked={formData.preferredContact === 'phone'}
                      onChange={handleChange}
                    />
                    <span>Phone</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="whatsapp"
                      checked={formData.preferredContact === 'whatsapp'}
                      onChange={handleChange}
                    />
                    <span>WhatsApp</span>
                  </label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
                data-testid="contact-submit"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Enquiry
                  </>
                )}
              </Button>

              <p className="form-note">
                We&apos;ll respond within 24 hours. For urgent matters, please call or WhatsApp us directly.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
